import { useTheme } from '@mui/material';

import {
  CohortSummaryAttributeResult,
  CohortCharacteristicsResultType,
} from '@verantos-dev/types-cohorts';

import { BarChart } from './BarChart';

export type ClinicalCharacteristicsChartProps = {
  data: CohortSummaryAttributeResult[] | undefined;
  resultType?: CohortCharacteristicsResultType;
  'data-testid'?: string;
};

export const ClinicalCharacteristicsChart = ({
  data,
  resultType,
  'data-testid': dataTestId,
}: ClinicalCharacteristicsChartProps) => {
  const theme = useTheme();

  if (!data) return null;

  const getBarColor = (type?: CohortCharacteristicsResultType) => {
    if (!type) return theme.palette.tertiary.darkBlue;

    switch (type) {
      case CohortCharacteristicsResultType.CC_CONDITION:
        return theme.palette.tertiary.blue;
      case CohortCharacteristicsResultType.CC_DRUG:
        return theme.palette.tertiary.lightBlue;
      case CohortCharacteristicsResultType.CC_MEASUREMENT:
        return theme.palette.tertiary.green;
      case CohortCharacteristicsResultType.CC_OBSERVATION:
        return theme.palette.tertiary.cyan;
      case CohortCharacteristicsResultType.CC_PROCEDURE:
        return theme.palette.tertiary.yellow;
      case CohortCharacteristicsResultType.CC_VISIT:
        return theme.palette.tertiary.orange;
      default:
        return theme.palette.tertiary.darkBlue;
    }
  };

  const barColor = getBarColor(resultType);

  const trimLabel = (label: string, maxLength = 30) => {
    return label.length > maxLength ? `${label.slice(0, maxLength)}...` : label;
  };

  return (
    <BarChart
      data={data}
      margin={{ left: 200 }}
      yAxis={[
        {
          scaleType: 'band',
          dataKey: 'attributeName',
          hideTooltip: true,
          valueFormatter: (value) => trimLabel(value, 30),
        },
      ]}
      layout="horizontal"
      xAxis={[
        {
          label: 'Count (%)',
          min: 0,
          max: 100,
          hideTooltip: true,
        },
      ]}
      series={[
        {
          dataKey: 'attributePercentage',
          color: barColor,
          valueFormatter: (item: any, context: any) => {
            const attributeName = data[context.dataIndex].attributeName;
            const attributeCount = data[context.dataIndex].attributeCount.toLocaleString();
            const attributePercentage = data[context.dataIndex].attributePercentage || 0;
            return `${attributeName}\u00A0\u00A0${attributeCount}  (${attributePercentage}%)`;
          },
        },
      ]}
      slotProps={{
        legend: { hidden: true },
        popper: {
          sx: {
            '& .MuiChartsTooltip-labelCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              '& .MuiTypography-root': {
                color: `${theme.palette.text.secondary}  !important`,
              },
            },
            '& .MuiChartsTooltip-valueCell': {
              backgroundColor: `${theme.palette.white.main} !important`,
              paddingLeft: '5px !important',
              '& .MuiTypography-root': {
                color: `${theme.palette.text.primary}  !important`,
              },
            },
          },
        },
      }}
      data-testid={dataTestId || `cohortCharacteristic-graph-bar-chart-${resultType}`}
    />
  );
};
